import { useMutation } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CopyableText } from "components/CopyableText";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { HarmonizedViolationResolutionStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_STAFF_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_GOV_STATUS,
  SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE,
} from "hooks/useHarmonizedViolations";
import { ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { assigneeDisplayName, type ViolationRow } from "./columns";
import {
  formatViolationDate,
  isViolationStale,
  MetricInfoIcon,
  PROGRAM_EXPLANATION,
  ProgramChip,
  SEVERITY_EXPLANATION,
  SeverityChip,
  STALE_EXPLANATION,
} from "./violationDisplay";

type ResolutionStatusValue = HarmonizedViolationResolutionStatus | "";

/** Narrows an arbitrary select value to a valid resolution status enum (or null). */
function toResolutionStatus(value: string): HarmonizedViolationResolutionStatus | null {
  return Object.values(HarmonizedViolationResolutionStatus).find((s) => s === value) ?? null;
}

/**
 * Editable, user-controlled "Resolution status" — backed by the `govStatus`
 * field, which (unlike EPA's derived `standardizedResolutionStatus`) is
 * preserved across data refreshes, so a user's "Resolved" sticks. EPA's own
 * derived status is shown beside it as read-only context.
 *
 * Mounted with `key={violation.id}` so its local state resets cleanly when a
 * different violation opens (no prop-sync effect). The mutation returns
 * { id, govStatus }; Apollo normalizes by id, so the grid row updates from cache.
 */
function ResolutionStatusEditor({
  violationId,
  initial,
  epaStatus,
  tenantId,
}: {
  violationId: string;
  initial: ResolutionStatusValue;
  epaStatus: HarmonizedViolationResolutionStatus;
  tenantId: string;
}) {
  const alerts = useAlerts();
  const [value, setValue] = useState<ResolutionStatusValue>(initial);
  const [setGovStatusMutation, { loading: saving, error: saveError }] = useMutation(
    SET_HARMONIZED_VIOLATION_GOV_STATUS
  );

  const handleChange = async (raw: string) => {
    const next = toResolutionStatus(raw);
    const previous = value;
    setValue(next ?? "");
    try {
      await setGovStatusMutation({ variables: { tenantId, id: violationId, govStatus: next } });
      alerts.success("Resolution status updated");
    } catch (e) {
      // Roll the field back to its last-saved value so it doesn't show a change
      // that didn't persist.
      setValue(previous);
      alerts.error("Could not save the resolution status. Try again.", e);
    }
  };

  return (
    <TextField
      select
      fullWidth
      size="small"
      label="Resolution status"
      value={value}
      disabled={saving}
      error={Boolean(saveError)}
      helperText={
        saveError
          ? "Could not save the resolution status. Try again."
          : `Reported status: ${prettyPrintEnumValue(epaStatus)}`
      }
      onChange={(e) => void handleChange(e.target.value)}
    >
      <MenuItem value="">—</MenuItem>
      {Object.values(HarmonizedViolationResolutionStatus).map((status) => (
        <MenuItem key={status} value={status}>
          {prettyPrintEnumValue(status)}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** First selected user id from the single-select picker, or null when cleared. */
function firstUserId(users: UserOption[] | null): string | null {
  return users && users.length > 0 ? (users[0]?.id ?? null) : null;
}

/**
 * Assignee section: a single-select user picker for the per-violation OVERRIDE
 * plus a "set as facility default" control. Mounted with `key={violation.id}` by
 * the drawer so picker state resets when a different violation opens (mirrors
 * GovStatusEditor's keyed-no-effect pattern).
 *
 * The per-violation mutation returns { id, assignee, assigneeIsFacilityDefault }
 * so Apollo normalizes the row by id and the grid updates from cache. The
 * facility-default mutation can change OTHER inherited rows' effective assignee,
 * which Apollo can't infer, so it refetches the list query.
 */
function AssigneeEditor({ violation, tenantId }: { violation: ViolationRow; tenantId: string }) {
  const initialOverride: UserOption[] | null =
    violation.assignee && !violation.assigneeIsFacilityDefault
      ? [
          {
            id: violation.assignee.id,
            email: violation.assignee.email,
            name: assigneeDisplayName(violation.assignee),
          },
        ]
      : null;

  const alerts = useAlerts();
  const [override, setOverride] = useState<UserOption[] | null>(initialOverride);

  const [setAssignee, { loading: savingOverride }] = useMutation(SET_HARMONIZED_VIOLATION_ASSIGNEE);
  const [setFacilityDefault, { loading: savingDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
    {
      // The grid can't infer which inherited rows changed, so refetch the list.
      refetchQueries: [HARMONIZED_VIOLATIONS],
      awaitRefetchQueries: true,
    }
  );

  const selectedUserId = firstUserId(override);

  const handleOverrideChange = async (users: UserOption[] | null) => {
    setOverride(users);
    try {
      await setAssignee({ variables: { tenantId, id: violation.id, userId: firstUserId(users) } });
      alerts.success(firstUserId(users) ? "Assignee updated" : "Assignee override cleared");
    } catch (e) {
      alerts.error("Could not save the assignee. Try again.", e);
    }
  };

  const handleSetDefault = async () => {
    // The button is disabled without a selection; guard anyway for safety.
    if (!selectedUserId) return;
    try {
      await setFacilityDefault({
        variables: { tenantId, facilityId: violation.facility.id, userId: selectedUserId },
      });
      alerts.success(`Default assignee set for ${violation.facility.name}`);
    } catch (e) {
      alerts.error("Could not set the facility default assignee. Try again.", e);
    }
  };

  const effectiveLabel = violation.assignee
    ? assigneeDisplayName(violation.assignee)
    : "Unassigned";

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Assignee</Typography>
      <MultipleUserPicker
        label="Assignee (override)"
        multiple={false}
        value={override}
        disabled={savingOverride}
        onChange={(users) => void handleOverrideChange(users)}
      />
      <Typography variant="caption" color="text.secondary">
        {violation.assigneeIsFacilityDefault
          ? `Inherited from the facility default: ${effectiveLabel}`
          : `Effective assignee: ${effectiveLabel}`}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={savingDefault || !selectedUserId}
        onClick={() => void handleSetDefault()}
        sx={{ alignSelf: "flex-start" }}
      >
        Set as default for {violation.facility.name}
      </Button>
      {!selectedUserId ? (
        <Typography variant="caption" color="text.secondary">
          Select an assignee above to set it as this facility's default.
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * Staff-only counterpart to {@link AssigneeEditor}: a single-select STAFF user
 * picker for the per-violation staff OVERRIDE plus a "set as staff default"
 * control. Backed by `staffUsers` (Encamp employees) via the picker's
 * `staffOnly` mode — the backend mutations reject non-staff target users.
 *
 * Mounted with `key={violation.id}` by the drawer so picker state resets when a
 * different violation opens. The per-violation mutation returns
 * { id, staffAssignee, staffAssigneeIsFacilityDefault } so Apollo normalizes the
 * row by id; the facility-default mutation can change OTHER inherited rows, so
 * it refetches the list query. Rendered only for staff callers.
 */
function StaffAssigneeEditor({
  violation,
  tenantId,
}: {
  violation: ViolationRow;
  tenantId: string;
}) {
  const initialOverride: UserOption[] | null =
    violation.staffAssignee && !violation.staffAssigneeIsFacilityDefault
      ? [
          {
            id: violation.staffAssignee.id,
            email: violation.staffAssignee.email,
            name: assigneeDisplayName(violation.staffAssignee),
          },
        ]
      : null;

  const alerts = useAlerts();
  const [override, setOverride] = useState<UserOption[] | null>(initialOverride);

  const [setStaffAssignee, { loading: savingOverride }] = useMutation(
    SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE
  );
  const [setFacilityDefault, { loading: savingDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_STAFF_ASSIGNEE,
    {
      // The grid can't infer which inherited rows changed, so refetch the list.
      refetchQueries: [HARMONIZED_VIOLATIONS],
      awaitRefetchQueries: true,
    }
  );

  const selectedUserId = firstUserId(override);

  const handleOverrideChange = async (users: UserOption[] | null) => {
    setOverride(users);
    try {
      await setStaffAssignee({
        variables: { tenantId, id: violation.id, userId: firstUserId(users) },
      });
      alerts.success(
        firstUserId(users) ? "Staff assignee updated" : "Staff assignee override cleared"
      );
    } catch (e) {
      alerts.error("Could not save the staff assignee. Try again.", e);
    }
  };

  const handleSetDefault = async () => {
    // The button is disabled without a selection; guard anyway for safety.
    if (!selectedUserId) return;
    try {
      await setFacilityDefault({
        variables: { tenantId, facilityId: violation.facility.id, userId: selectedUserId },
      });
      alerts.success(`Default staff assignee set for ${violation.facility.name}`);
    } catch (e) {
      alerts.error("Could not set the facility default staff assignee. Try again.", e);
    }
  };

  const effectiveLabel = violation.staffAssignee
    ? assigneeDisplayName(violation.staffAssignee)
    : "Unassigned";

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Staff assignee</Typography>
      <MultipleUserPicker
        label="Staff assignee (override)"
        multiple={false}
        staffOnly
        value={override}
        disabled={savingOverride}
        onChange={(users) => void handleOverrideChange(users)}
      />
      <Typography variant="caption" color="text.secondary">
        {violation.staffAssigneeIsFacilityDefault
          ? `Inherited from the facility staff default: ${effectiveLabel}`
          : `Effective staff assignee: ${effectiveLabel}`}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={savingDefault || !selectedUserId}
        onClick={() => void handleSetDefault()}
        sx={{ alignSelf: "flex-start" }}
      >
        Set as staff default for {violation.facility.name}
      </Button>
      {!selectedUserId ? (
        <Typography variant="caption" color="text.secondary">
          Select a staff assignee above to set it as this facility's default.
        </Typography>
      ) : null}
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Stack>
  );
}

/** A small uppercase section label dividing the drawer into Tracking / Details. */
function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Typography variant="overline" color="text.primary" sx={{ mt: 1, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

export function ViolationDetailDrawer({
  violation,
  tenantId,
  refreshStartedAt,
  onClose,
}: {
  violation: ViolationRow | null;
  tenantId: string;
  /** Start of the latest successful refresh — used to flag a stale violation. */
  refreshStartedAt?: string | null;
  onClose: () => void;
}) {
  const { isStaff } = useCurrentUser();
  if (!violation) {
    return <Drawer anchor="right" open={false} onClose={onClose} />;
  }

  const stale = isViolationStale(violation.lastSeenAt, refreshStartedAt);
  const facilityProfilePath = `/o/${tenantId}/facilities/${violation.facility.id}/profile`;

  return (
    <Drawer anchor="right" open onClose={onClose}>
      <Box
        sx={{ width: { xs: "100vw", sm: 480 }, p: 3 }}
        role="region"
        aria-label="Violation detail"
      >
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">
              Violation{" "}
              <Typography component="span" variant="inherit" fontWeight="bold">
                {violation.violationId}
              </Typography>
            </Typography>
            <Link
              component={RouterLink}
              to={facilityProfilePath}
              target="_blank"
              rel="noopener"
              variant="body2"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {violation.facility.name} <OpenInNew fontSize="inherit" />
            </Link>
            {violation.violationDescription ? (
              <Typography variant="body2" color="text.secondary">
                {violation.violationDescription}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} aria-label="Close violation detail">
            <Close />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
          <ProgramChip program={violation.sourceProgram} />
          <MetricInfoIcon explanation={PROGRAM_EXPLANATION} label="program" />
          <SeverityChip severity={violation.standardizedSeverity} />
          <MetricInfoIcon explanation={SEVERITY_EXPLANATION} label="severity" />
          {stale ? (
            <>
              <Chip size="small" color="warning" variant="outlined" label="Stale" />
              <MetricInfoIcon explanation={STALE_EXPLANATION} label="stale" />
            </>
          ) : null}
        </Stack>

        <SectionHeading>Tracking</SectionHeading>
        <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
          <ResolutionStatusEditor
            key={`status-${violation.id}`}
            violationId={violation.id}
            initial={violation.govStatus ?? ""}
            epaStatus={violation.standardizedResolutionStatus}
            tenantId={tenantId}
          />
          <AssigneeEditor
            key={`assignee-${violation.id}`}
            violation={violation}
            tenantId={tenantId}
          />
          {isStaff ? (
            <StaffAssigneeEditor
              key={`staff-assignee-${violation.id}`}
              violation={violation}
              tenantId={tenantId}
            />
          ) : null}
        </Stack>

        <SectionHeading>Details</SectionHeading>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Field label="Violation code" value={violation.violationCode} />
          <Field label="Severity reason" value={violation.stdSeverityDeterminantDesc} />
          <Field label="Resolution method" value={violation.resolutionMethodDesc} />
          <Field label="Schedule event" value={violation.scheduleEventDesc} />
          <Field label="Pollutant" value={violation.pollutantTypeDesc} />
          <Field label="Citations" value={violation.citations} />
          <Field label="Citation descriptions" value={violation.citationDescs} />
          <Field label="Agency" value={violation.violationAgency} />
          <Field label="Agency level" value={violation.violationAgencyLevel} />
          <Field
            label="EPA facility ID"
            value={
              violation.epaFacilityId ? (
                <CopyableText>{violation.epaFacilityId}</CopyableText>
              ) : null
            }
          />
          <Field
            label="Registry ID"
            value={
              violation.registryId ? <CopyableText>{violation.registryId}</CopyableText> : null
            }
          />
          <Field label="State" value={violation.state} />
          <Field label="Started" value={formatViolationDate(violation.violationStartDate)} />
          <Field label="Ended" value={formatViolationDate(violation.violationEndDate)} />
          <Field label="Last seen" value={formatViolationDate(violation.lastSeenAt)} />

          {violation.siteCount > 1 ? (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Affected sites ({violation.siteCount})</Typography>
              {violation.affectedFacilities.map((f) => (
                <Link
                  key={f.id}
                  component={RouterLink}
                  to={`/o/${tenantId}/facilities/${f.id}/profile`}
                  target="_blank"
                  rel="noopener"
                  variant="body2"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  {f.name} <OpenInNew fontSize="inherit" />
                </Link>
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {violation.detailLink ? (
              <Button
                component="a"
                href={violation.detailLink}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="outlined"
                endIcon={<OpenInNew />}
              >
                View in ECHO
              </Button>
            ) : null}
            {violation.epaFacilityLink ? (
              <Button
                component="a"
                href={violation.epaFacilityLink}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="outlined"
                endIcon={<OpenInNew />}
              >
                Facility in ECHO
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
